<template>
  <Teleport to="body">
    <!-- 6 posisi -->
    <div
      v-for="pos in positions"
      :key="pos.key"
      class="toast-container-modern position-fixed p-3 pe-none"
      :class="pos.bs"
      aria-live="polite"
      aria-atomic="true"
    >
      <TransitionGroup name="toast-slide" tag="div">
        <div
          v-for="t in toastsBy(pos.key)"
          :key="t.id"
          class="toast-modern show w-100 border-0 shadow pe-auto"
          role="alert"
          aria-live="assertive"
          aria-atomic="true"
          :class="themeClass(t.type)"
        >
          <!-- Icon and Content -->
          <div class="toast-content">
            <div class="toast-icon">
              <i :class="getIconClass(t.type)"></i>
            </div>
            <div class="toast-body-modern flex-grow-1">
              <div class="toast-message">{{ t.message }}</div>
              <div v-if="t.timeout > 0" class="toast-timer">
                {{ formatTime(t) }}
              </div>
            </div>
            <button
              v-if="t.dismissible"
              type="button"
              class="btn-close-modern"
              :class="closeBtnClass(t.type)"
              aria-label="Close"
              @click="remove(t.id)"
            >
              <i class="bi bi-x-lg"></i>
            </button>
          </div>

          <!-- Progress Bar -->
          <div v-if="t.timeout > 0" class="progress-container-modern">
            <div class="progress-bar-modern">
              <div
                class="progress-fill-modern"
                :class="progressBarClass(t.type)"
                :style="{ width: progressWidth(t) + '%', transition: 'width 100ms linear' }"
                role="progressbar"
                :aria-valuenow="progressWidth(t)"
                aria-valuemin="0"
                aria-valuemax="100"
              ></div>
            </div>
          </div>
        </div>
      </TransitionGroup>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue'
import { useToastStore } from '@/components/helper/toast'
import '@/components/style/Toaster.css'

const store = useToastStore()

const positions = [
  { key: 'top-left',      bs: ['top-0', 'start-0'] },
  { key: 'top-center',    bs: ['top-0', 'start-50', 'translate-middle-x'] },
  { key: 'top-right',     bs: ['top-0', 'end-0'] },
  { key: 'bottom-left',   bs: ['bottom-0', 'start-0'] },
  { key: 'bottom-center', bs: ['bottom-0', 'start-50', 'translate-middle-x'] },
  { key: 'bottom-right',  bs: ['bottom-0', 'end-0'] },
]

// reaktif: filter langsung tanpa computed.value
const toastsBy = (pos) =>
  store.toasts.filter(
    t => t.position === pos && (t.variant ?? 'default') === 'default'
  )
const remove   = (id) => store.remove(id)

// Modern theme classes with gradients
const themeClass = (type) => {
  switch (type) {
    case 'success':   return ['toast-success']
    case 'danger':    return ['toast-danger']
    case 'warning':   return ['toast-warning']
    case 'info':      return ['toast-info']
    case 'primary':   return ['toast-primary']
    case 'secondary': return ['toast-secondary']
    case 'dark':      return ['toast-dark']
    case 'light':     return ['toast-light']
    default:          return ['toast-secondary']
  }
}

// Icon classes for different toast types
const getIconClass = (type) => {
  switch (type) {
    case 'success':   return 'bi bi-check-circle-fill'
    case 'danger':    return 'bi bi-x-circle-fill'
    case 'warning':   return 'bi bi-exclamation-triangle-fill'
    case 'info':      return 'bi bi-info-circle-fill'
    case 'primary':   return 'bi bi-bell-fill'
    case 'secondary': return 'bi bi-gear-fill'
    case 'dark':      return 'bi bi-moon-fill'
    case 'light':     return 'bi bi-sun-fill'
    default:          return 'bi bi-bell-fill'
  }
}

const isDarkBg = (type) => ['success','danger','primary','secondary','dark'].includes(type)
const closeBtnClass = (type) => (isDarkBg(type) ? 'btn-close-white' : 'btn-close-dark')
const progressBarClass = (type) => (isDarkBg(type) ? 'progress-white' : 'progress-dark')

// Format remaining time
function formatTime(t) {
  if (!t.timeout || t.timeout <= 0) return ''
  const elapsed = Math.max(0, now.value - (t.startedAt || 0))
  const remain = Math.max(0, t.timeout - elapsed)
  const seconds = Math.ceil(remain / 1000)
  return `${seconds}s`
}

// timer kecil untuk update progress
const now = ref(Date.now())
let tick = null
onMounted(() => { tick = setInterval(() => (now.value = Date.now()), 100) })
onBeforeUnmount(() => { clearInterval(tick) })

// width (%) sisa waktu (mengecil sampai 0)
function progressWidth(t) {
  if (!t.timeout || t.timeout <= 0) return 0
  const elapsed = Math.max(0, now.value - (t.startedAt || 0))
  const remain = Math.max(0, t.timeout - elapsed)
  return Math.round((remain / t.timeout) * 100)
}
</script>
